// Copyright (c) Ping. All rights reserved.

import React from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  type TextStyle,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { createTheme } from '../../design-system/theme';
import type { TaskFolder } from './types';

export interface TaskFolderRowProps {
  readonly folder: TaskFolder;
  readonly open: boolean;
  readonly onToggle: (folder: TaskFolder) => void;
}

/** Collapsible folder header. Tap toggles open/closed. */
export const TaskFolderRow = React.memo<TaskFolderRowProps>(({ folder, open, onToggle }) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');

  return (
    <Pressable
      onPress={() => onToggle(folder)}
      accessibilityRole="button"
      accessibilityLabel={folder.title}
      accessibilityState={{ expanded: open }}
      style={({ pressed }) => [
        styles.row,
        {
          backgroundColor: pressed ? theme.colors.surface.tertiary : theme.colors.surface.secondary,
          borderBottomColor: theme.colors.border.secondary,
        },
      ]}
    >
      <Text style={[styles.chevron, { color: theme.colors.text.secondary }]}>
        {open ? '▾' : '▸'}
      </Text>
      <Text style={[styles.title, { color: theme.colors.text.primary }]} numberOfLines={1}>
        {folder.title}
      </Text>
      <Text style={[styles.count, { color: theme.colors.text.tertiary }]}>{folder.taskCount}</Text>
    </Pressable>
  );
});

TaskFolderRow.displayName = 'TaskFolderRow';

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
  chevron: {
    fontSize: 14,
    width: 16,
    textAlign: 'center',
    marginRight: 8,
  } satisfies TextStyle,
  title: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
  } satisfies TextStyle,
  count: {
    fontSize: 12,
    fontWeight: '500',
    marginLeft: 8,
  } satisfies TextStyle,
});
